import { ContentLayout, Header } from '@amzn/awsui-components-core';
import React, { useEffect, useRef } from 'react';
import { useLayoutConfig } from '../../contexts/LayoutContext';
import { BreadcrumbItem } from '../../types/layout/layout.types';

interface ReportsLayoutProps {
  children: React.ReactNode;
  title?: string;
  actions?: React.ReactNode;
  breadcrumbItems?: BreadcrumbItem[];
  splitPanel?: React.ReactNode;
  splitPanelOpen?: boolean;
}

// Move default breadcrumb items outside component to prevent recreation on every render
const DEFAULT_BREADCRUMB_ITEMS: BreadcrumbItem[] = [{ text: 'Home', href: '/' }];

/**
 * Shared layout for Reports pages
 * Uses data-driven approach for layout configuration
 */
export const ReportsLayout: React.FC<ReportsLayoutProps> = ({
  children,
  title,
  actions,
  breadcrumbItems = DEFAULT_BREADCRUMB_ITEMS,
  splitPanel,
  splitPanelOpen = false,
}): JSX.Element => {
  const { setConfig } = useLayoutConfig();

  // Track previous values to detect actual changes
  const prevBreadcrumbsRef = useRef<string>('');
  const prevSplitPanelOpenRef = useRef<boolean>(false);
  const prevHasSplitPanelRef = useRef<boolean>(false);

  // Configure layout when meaningful values change
  useEffect(() => {
    const breadcrumbsSerialized = JSON.stringify(breadcrumbItems);
    const hasSplitPanel = !!splitPanel;
    const breadcrumbsChanged = prevBreadcrumbsRef.current !== breadcrumbsSerialized;
    const splitPanelOpenChanged = prevSplitPanelOpenRef.current !== splitPanelOpen;
    const splitPanelChanged = prevHasSplitPanelRef.current !== hasSplitPanel;
    const isInitialRender = prevBreadcrumbsRef.current === '';

    // Only update if something actually changed
    if (!isInitialRender && !breadcrumbsChanged && !splitPanelOpenChanged && !splitPanelChanged) {
      return;
    }

    prevBreadcrumbsRef.current = breadcrumbsSerialized;
    prevSplitPanelOpenRef.current = splitPanelOpen;
    prevHasSplitPanelRef.current = hasSplitPanel;

    setConfig({
      navigationType: 'home',
      navigationHide: false,
      breadcrumbs: breadcrumbItems,
      tools: { hide: true },
      splitPanel: {
        component: splitPanel,
        open: splitPanelOpen,
      },
      maxContentWidth: Number.MAX_VALUE,
    });
  }, [setConfig, breadcrumbItems, splitPanel, splitPanelOpen]);

  return (
    <div className="report-view-page">
      <ContentLayout header={<Header actions={actions}>{title}</Header>}>{children}</ContentLayout>
    </div>
  );
};
